import React from 'react';
import { Row, Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import ResourceRequestFormSection from './ResourceRequestFormSection';
import FormTextAreaField from '../../formFields/FormTextAreaField';
import FormDateTimeField from '../../formFields/FormDateTimeField';
import FormTextField from '../../formFields/FormTextField';
import FormSelectField from '../../formFields/FormSelectField';
import FormGeoLocationField from '../../formFields/FormGeoLocationField';
import { ResourceRequest } from '../../hooks/incidentResourceRequestHooks';
import { useResourceSites } from '../../hooks/resourceSiteHooks';
import {
  ResourceRequestFormFields,
  ApprovalStage,
} from '../utils/ResourceRequestTypes';
import { isStageCompleted } from '../utils/ResourceRequestUtils';
import dayjs from 'dayjs';
import { useCurrentUserApprovalAuthority } from '../../../../hooks/incident/useCurrentUserApprovalAuthority';

interface LogisticsApprovalSectionProps {
  requestData: ResourceRequest | null;
  currentStage: ApprovalStage;
  onApprove: () => void;
  isSubmitting: boolean;
  isCompleted: boolean;
}

const LogisticsApprovalSection: React.FC<LogisticsApprovalSectionProps> = ({
  requestData,
  currentStage,
  onApprove,
  isSubmitting,
  isCompleted,
}) => {
  const { register, watch, setValue } =
    useFormContext<ResourceRequestFormFields>();
  const { data: resourceSites } = useResourceSites();
  const stageCompleted = isStageCompleted('logistics', requestData);
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();

  // Resource site options for source location
  const siteOptions =
    resourceSites?.map((site) => ({
      value: site.id,
      label: site.name,
    })) || [];

  return (
    <ResourceRequestFormSection
      stage="logistics"
      currentStage={currentStage}
      isCompleted={stageCompleted}
      completedBy={{
        name: requestData?.logistics_name,
        position: requestData?.logistics_position,
        signature: requestData?.logistics_signature,
        timestamp: requestData?.logistics_signature_timestamp,
      }}
      onApprove={stageCompleted ? undefined : onApprove}
      isSubmitting={isSubmitting}
      hasApprovalAuthority={hasApprovalAuthority}
    >
      {stageCompleted ? (
        <>
          <Row>
            <div className="col-6">
              <label className="form-label">Order Number</label>
              <input
                className="form-control"
                value={watch('logistics_order_number')}
                disabled
              />
            </div>
            <div className="col-6">
              <label className="form-label">ETA (LSC)</label>
              <input
                className="form-control"
                type="datetime-local"
                value={
                  watch('logistics_eta')
                    ? dayjs(watch('logistics_eta')).format('YYYY-MM-DDTHH:mm')
                    : ''
                }
                disabled
              />
            </div>
          </Row>
          <Row>
            <div className="col-6">
              <label className="form-label">Cost (LSC)</label>
              <input
                className="form-control"
                value={watch('logistics_cost') || ''}
                disabled
              />
            </div>
            <div className="col-6">
              <label className="form-label">Supplier Name</label>
              <input
                className="form-control"
                value={watch('supplier_name')}
                disabled
              />
            </div>
          </Row>
          <Row>
            <div className="col-6">
              <label className="form-label">Phone</label>
              <input
                className="form-control"
                value={watch('supplier_phone')}
                disabled
              />
            </div>
            <div className="col-6">
              <label className="form-label">Email</label>
              <input
                className="form-control"
                value={watch('supplier_email')}
                disabled
              />
            </div>
          </Row>
          <Row>
            <div className="col-12">
              <label className="form-label">Source Location</label>
              {requestData?.source_location_id ? (
                <input
                  className="form-control"
                  value={
                    resourceSites?.find(
                      (s) => s.id === requestData.source_location_id
                    )?.name || 'Unknown Site'
                  }
                  disabled
                />
              ) : requestData?.source_location_geolocation ? (
                <input
                  className="form-control"
                  value={
                    (
                      requestData.source_location_geolocation as {
                        address?: string;
                      }
                    )?.address || 'Custom Location'
                  }
                  disabled
                />
              ) : (
                <input
                  className="form-control"
                  value="Not specified"
                  disabled
                />
              )}
            </div>
          </Row>
          <Row>
            <div className="col-6">
              <label className="form-label">Order Placed By</label>
              <div>
                <Form.Check
                  type="radio"
                  label="SPUL"
                  checked={watch('logistics_order_placed_by') === 'SPUL'}
                  disabled
                />
                <Form.Check
                  type="radio"
                  label="PROC"
                  checked={watch('logistics_order_placed_by') === 'PROC'}
                  disabled
                />
                <Form.Check
                  type="radio"
                  label="Other"
                  checked={watch('logistics_order_placed_by') === 'Other'}
                  disabled
                />
                {watch('logistics_order_placed_by') === 'Other' && (
                  <input
                    className="form-control mt-2"
                    placeholder="Specify other..."
                    value={watch('logistics_order_placed_by_other') || ''}
                    disabled
                  />
                )}
              </div>
            </div>
          </Row>
          <Row>
            <div className="col-12">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control"
                value={watch('logistics_notes')}
                disabled
                rows={3}
              />
            </div>
          </Row>
        </>
      ) : (
        <>
          <Row>
            <FormTextField
              fieldName="logistics_order_number"
              title="Order Number"
              variant="half"
              disabled={!hasApprovalAuthority}
            />
            {isCompleted ? (
              <div className="col-6 mt-2">
                <label className="form-label">ETA (LSC)</label>
                <input
                  className="form-control"
                  type="datetime-local"
                  {...register('logistics_eta')}
                  disabled={isCompleted || !hasApprovalAuthority}
                />
              </div>
            ) : (
              <FormDateTimeField
                fieldName="logistics_eta"
                title="ETA (LSC)"
                variant="half"
                type="datetime-local"
                disabled={!hasApprovalAuthority}
              />
            )}
          </Row>
          <Row>
            <FormTextField
              fieldName="logistics_cost"
              title="Cost (LSC)"
              variant="half"
              rules={{
                valueAsNumber: true,
              }}
              disabled={!hasApprovalAuthority}
            />
            <FormTextField
              fieldName="supplier_name"
              title="Supplier Name"
              variant="half"
              disabled={!hasApprovalAuthority}
            />
          </Row>
          <Row>
            <FormTextField
              fieldName="supplier_phone"
              title="Phone"
              variant="half"
              disabled={!hasApprovalAuthority}
            />
            <FormTextField
              fieldName="supplier_email"
              title="Email"
              variant="half"
              disabled={!hasApprovalAuthority}
            />
          </Row>
          <Row>
            <div className="col-12 mt-2">
              <Form.Check
                type="switch"
                id="custom-source-location"
                label="Use custom source location instead of resource site"
                checked={watch('use_custom_source_location') || false}
                onChange={
                  hasApprovalAuthority
                    ? (e) =>
                        setValue('use_custom_source_location', e.target.checked)
                    : undefined
                }
                className="mb-2"
                disabled={isCompleted || !hasApprovalAuthority}
              />
            </div>
          </Row>
          <Row>
            <div className="col-12">
              {watch('use_custom_source_location') ? (
                <FormGeoLocationField
                  name="source_location_geolocation"
                  label="Source Location"
                  disabled={isCompleted || !hasApprovalAuthority}
                />
              ) : isCompleted ? (
                <div className="mt-2">
                  <label className="form-label">Source Location</label>
                  <select
                    className="form-control"
                    {...register('source_location_id')}
                    disabled={isCompleted || !hasApprovalAuthority}
                  >
                    <option value="">Select a resource site...</option>
                    {siteOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <FormSelectField
                  fieldName="source_location_id"
                  title="Source Location"
                  options={siteOptions}
                  variant="full"
                  disabled={!hasApprovalAuthority}
                />
              )}
            </div>
          </Row>
          <Row>
            <div className="col-6">
              <label className="form-label">Order Placed By</label>
              <div>
                <Form.Check
                  type="radio"
                  name="logistics_order_placed_by"
                  label="SPUL"
                  checked={watch('logistics_order_placed_by') === 'SPUL'}
                  onChange={
                    hasApprovalAuthority
                      ? (e) =>
                          setValue(
                            'logistics_order_placed_by',
                            e.target.checked ? 'SPUL' : ''
                          )
                      : undefined
                  }
                  disabled={isCompleted || !hasApprovalAuthority}
                />
                <Form.Check
                  type="radio"
                  name="logistics_order_placed_by"
                  label="PROC"
                  checked={watch('logistics_order_placed_by') === 'PROC'}
                  onChange={
                    hasApprovalAuthority
                      ? (e) =>
                          setValue(
                            'logistics_order_placed_by',
                            e.target.checked ? 'PROC' : ''
                          )
                      : undefined
                  }
                  disabled={isCompleted || !hasApprovalAuthority}
                />
                <Form.Check
                  type="radio"
                  name="logistics_order_placed_by"
                  label="Other"
                  checked={watch('logistics_order_placed_by') === 'Other'}
                  onChange={
                    hasApprovalAuthority
                      ? (e) =>
                          setValue(
                            'logistics_order_placed_by',
                            e.target.checked ? 'Other' : ''
                          )
                      : undefined
                  }
                  disabled={isCompleted || !hasApprovalAuthority}
                />
                {watch('logistics_order_placed_by') === 'Other' && (
                  <input
                    className="form-control mt-2"
                    placeholder="Specify other..."
                    {...register('logistics_order_placed_by_other')}
                    disabled={isCompleted || !hasApprovalAuthority}
                  />
                )}
              </div>
            </div>
          </Row>
          <Row>
            {isCompleted ? (
              <div className="col-12 mt-2">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control"
                  {...register('logistics_notes')}
                  disabled={isCompleted || !hasApprovalAuthority}
                  rows={3}
                />
              </div>
            ) : (
              <FormTextAreaField
                fieldName="logistics_notes"
                title="Notes"
                variant="full"
                disabled={!hasApprovalAuthority}
              />
            )}
          </Row>
          <Row>
            <div className="col-6">
              <label className="form-label">Logistics Name</label>
              <input
                className="form-control"
                {...register('logistics_name')}
                value={watch('logistics_name') || ''}
                disabled
                readOnly
              />
            </div>
            <div className="col-6">
              <label className="form-label">Logistics Position</label>
              <input
                className="form-control"
                {...register('logistics_position')}
                value={watch('logistics_position') || ''}
                disabled
                readOnly
              />
            </div>
          </Row>
        </>
      )}
    </ResourceRequestFormSection>
  );
};

export default LogisticsApprovalSection;
